import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "material-ui/styles";
import Header from "../common/Header";
import NotificationBar from "../Parts/NotificationBar.jsx";
import TabsControlSection from "./sections/TabsControlSection";
import ResultHeaderSection from "./sections/ResultHeaderSection";
import ResultEmptySection from "./sections/ResultEmptySection";
import ResultSection from "./sections/ResultSection";
import ResultLoaderSection from "./sections/ResultLoaderSection";
import DownloadHistoryDetailDialog from "./dialogs/DownloadHistoryDetailDialog";
import { OwnerType } from "../../constants/ComponentTypes";
import { DOWNLOAD_HISTORY_PER_PAGE } from "../../constants/Constants";
import { canViewDownloadHistory } from "../../utils/utils";
import moment from "moment";

const styles = () => ({
  root: {
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
  },

  main: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
  },

  content: {
    width: "100%",
    boxSizing: "border-box",
    paddingTop: 40,
    overflowY: 'auto',
  },

  noPermission: {
    paddingTop: 80,
    fontSize: 16,
    color: "#181819",
  },

  notificationContainer: {
    position: "fixed",
    zIndex: 10000,
    top: 10,
    width: "98%",
    left: "1%",
  },
});

class DownloadHistory extends React.Component {
  initialState = {
    ownerType: OwnerType.OWN.value,
    selectedYear: "",
    selectedMonth: "",
    message: "",
    messageType: "success",
    showNotification: false,
    showDetailDialog: false,
    selectedOrder: null,
  };

  constructor(props) {
    super(props);
    const ownType = props.location && props.location.state && props.location.state.ownerType ? props.location.state.ownerType : OwnerType.OWN.value;
    const selectedYear = props.location && props.location.state && props.location.state.selectedYear ? props.location.state.selectedYear : moment().format('YYYY');
    const selectedMonth = props.location && props.location.state && props.location.state.selectedMonth ? props.location.state.selectedMonth : moment().format('M');
    this.state = { ...this.initialState, ...{ ownerType: ownType, selectedYear: selectedYear, selectedMonth: selectedMonth } };
  }

  componentDidMount() {
    this.props.initializeMessage();
    this.props.getUserInfo();
    // ユーザー情報が取得済みの場合のみ、閲覧権限を確認して取得する（未取得の場合は取得後に行う）
    if (this.props.user) {
      this.fetchIfPermitted(this.props.user);
    }
  }

  // 閲覧権限がある場合のみ、ダウンロード履歴を取得する（権限がない場合は403になるAPIを呼ばない）
  fetchIfPermitted = (user) => {
    if (!canViewDownloadHistory(user)) {
      return;
    }
    this.getDownloadHistory();
    this.getOverview();
  };

  // ユーザー情報を取得済みで、閲覧権限がない場合にtrueを返す
  hasNoPermission = () => {
    const { user } = this.props;
    return !!user && !canViewDownloadHistory(user);
  };

  componentWillReceiveProps(nextProps) {
    // ユーザー情報を取得したタイミングで、閲覧権限がある場合のみ取得する
    if (!this.props.user && nextProps.user) {
      this.fetchIfPermitted(nextProps.user);
    }

    if (nextProps.isLoading) {
      document.body.style.cursor = "progress";
    } else {
      document.body.style.cursor = "auto";
    }

    if (nextProps.message && nextProps.message !== "") {
      this.displayNotification(nextProps.message, "success");
    } else if (nextProps.errorMessage && nextProps.errorMessage !== "") {
      this.displayNotification(nextProps.errorMessage, "error");
    }
  }

  // 選択中の年月(YYYY-M)を返す
  formatSelectedYearMonth = () => {
    return this.state.selectedYear + '-' + this.state.selectedMonth;
  };

  // 選択中の年月から、APIに渡すinstructed_atの期間(Y-m-d)を返す
  buildInstructedAtRange = () => {
    const selectedYearMonth = moment(this.formatSelectedYearMonth(), 'YYYY-M');
    return {
      instructed_at_from: selectedYearMonth.startOf('month').format('YYYY-MM-DD'),
      instructed_at_to: selectedYearMonth.endOf('month').format('YYYY-MM-DD'),
    };
  };

  // 選択中のタブ(自分/チーム)をis_ownのクエリ値に変換する
  buildIsOwn = () => {
    return this.state.ownerType === OwnerType.OWN.value ? 'true' : 'false';
  };

  getDownloadHistory = (data = {}) => {
    let query = {
      is_own: this.buildIsOwn(),
      ...this.buildInstructedAtRange(),
      per_page: DOWNLOAD_HISTORY_PER_PAGE,
      page: 1,
    }
    this.props.getDownloadHistory({ ...query, ...data });
  };

  getOverview = () => {
    this.props.getDownloadHistoryOverview({
      is_own: this.buildIsOwn(),
      ...this.buildInstructedAtRange(),
    });
  };

  handleOwnerTypeChange = (value) => {
    this.setState({
      ownerType: value,
    }, () => {
      this.getDownloadHistory();
      this.getOverview();
    });
  };

  handleSelectedYearChange = (value) => {
    const { user } = this.props;
    const currentDate = moment();
    const changeYearMonth = moment(value + '-' + this.state.selectedMonth, 'YYYY-M');
    let state = {
      selectedYear: value,
    };

    if (changeYearMonth.isAfter(currentDate, 'month')) {
      // 年の変更で選択月が未来になる場合は今月に補正
      state.selectedMonth = currentDate.format('M');
    } else if (user) {
      const userCreatedAt = moment(user.created_at, 'YYYY-M');
      if (changeYearMonth.isBefore(userCreatedAt, 'month')) {
        // 年の変更で選択月が登録日より前になる場合は登録月に補正
        state.selectedMonth = userCreatedAt.format('M');
      }
    }

    this.setState(state, () => {
      this.getDownloadHistory();
      this.getOverview();
    });
  };

  handleSelectedMonthChange = (value) => {
    this.setState({
      selectedMonth: value,
    }, () => {
      this.getDownloadHistory();
      this.getOverview();
    });
  };

  // サマリー行から明細ダイアログを開き、対象注文の明細を取得する
  handleShowDetail = (order) => {
    this.setState({
      showDetailDialog: true,
      selectedOrder: order,
    });
    this.props.getDownloadHistoryDetail(order.instructed_at, order.ownership_user_id);
  };

  handleCloseDetail = () => {
    this.setState({
      showDetailDialog: false,
      selectedOrder: null,
    });
  };

  // 明細ダイアログのページャー操作時、対象注文の明細を指定ページで再取得する
  handleChangeDetailPage = (data) => {
    const { selectedOrder } = this.state;
    if (!selectedOrder) {
      return;
    }
    this.props.getDownloadHistoryDetail(
      selectedOrder.instructed_at,
      selectedOrder.ownership_user_id,
      data.page,
      data.per_page,
    );
  };

  displayNotification = (message, type) => {
    this.setState({
      showNotification: true,
      message: message,
      messageType: type,
    });
  };

  renderNotification = () => {
    const { classes } = this.props;
    if (this.state.showNotification) {
      return (
        <div className={classes.notificationContainer}>
          <NotificationBar
            message={this.state.message}
            type={this.state.messageType}
            open={this.state.showNotification}
            onHandleClose={() => {
              this.setState({ showNotification: false });
            }}
            autoClose={false}
            style={{
              width: this.state.messageType === "green" ? "fit-content" : "100%",
            }}
          ></NotificationBar>
        </div>
      );
    }
  }

  render() {
    const { classes, downloadHistory, isLoading, pageInfo, detail, detailPageInfo, isLoadingDetail, overview, isLoadingOverview } = this.props;

    if (this.hasNoPermission()) {
      return (
        <div className={classes.root}>
          <Header title="ダウンロード履歴" type="dark" hasBackBtn={false} />
          <div className={classes.main}>
            <div className={classes.noPermission}>
              ダウンロード履歴を閲覧する権限がありません。アカウント管理者にお問い合わせください。
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Header title="ダウンロード履歴" type="dark" hasBackBtn={false} />

        <div className={classes.main}>
          <TabsControlSection
            ownerType={this.state.ownerType}
            handleOwnerTypeChange={this.handleOwnerTypeChange}
          />

          <ResultHeaderSection
            selectedYear={this.state.selectedYear}
            selectedMonth={this.state.selectedMonth}
            overview={overview}
            isLoadingOverview={isLoadingOverview}
            handleSelectedYearChange={this.handleSelectedYearChange}
            handleSelectedMonthChange={this.handleSelectedMonthChange}
            isLoading={isLoading}
          />

          <div className={classes.content}>
            {(() => {
              if (isLoading) {
                return <ResultLoaderSection />;
              } else if (downloadHistory.size === 0) {
                return <ResultEmptySection />;
              } else {
                return <ResultSection
                  downloadHistory={downloadHistory}
                  pageInfo={pageInfo}
                  getDownloadHistory={this.getDownloadHistory}
                  isLoading={isLoading}
                  onShowDetail={this.handleShowDetail}
                />
              }
            })()}
            {this.renderNotification()}
          </div>
        </div>

        {this.state.showDetailDialog && (
          <DownloadHistoryDetailDialog
            isOpen={this.state.showDetailDialog}
            order={this.state.selectedOrder}
            detail={detail}
            pageInfo={detailPageInfo}
            isLoading={isLoadingDetail}
            onChangePage={this.handleChangeDetailPage}
            onClose={this.handleCloseDetail}
          />
        )}
      </div>
    );
  }
}

DownloadHistory.propTypes = {
  classes: PropTypes.object.isRequired,
  user: PropTypes.object,
  downloadHistory: PropTypes.object,
  pageInfo: PropTypes.object,
  isLoading: PropTypes.bool,
  detail: PropTypes.array,
  detailPageInfo: PropTypes.object,
  isLoadingDetail: PropTypes.bool,
  overview: PropTypes.object,
  isLoadingOverview: PropTypes.bool,
  getDownloadHistory: PropTypes.func.isRequired,
  getDownloadHistoryDetail: PropTypes.func.isRequired,
  getDownloadHistoryOverview: PropTypes.func.isRequired,
  initializeMessage: PropTypes.func.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  location: PropTypes.object,
  message: PropTypes.string,
  errorMessage: PropTypes.string,
};

export default withStyles(styles)(DownloadHistory);



// WEBPACK FOOTER //
// ./src/components/DownloadHistory/DownloadHistory.jsx